import type Task from "../../entities/task";

export const UPDATE_LOADING = 'UPDATE_LOADING';
export const LOGIN_ERROR_RECOGNIZED = 'LOGIN_ERROR_RECOGNIZED';
export const LOGIN = 'LOGIN';
export const LOGIN_RESULT = 'LOGIN_RESULT';
export const LOGIN_STATE = 'LOGIN_STATE';
export const LOGIN_RETRY_REQUESTED = 'LOGIN_RETRY_REQUESTED';
export const SET_LOADING_MESSAGE = 'SET_LOADING_MESSAGE';
export const SHOW_TASK = 'SHOW_TASK';
export const SHOW_TASK_HANDLED = 'SHOW_TASK_HANDLED';
export const SAVE_OPEN_TASK = 'SAVE_OPEN_TASK';
export const SET_RESTORE_STATUS = 'SET_RESTORE_STATUS';
export const SET_FIRM_ID = 'SET_FIRM_ID';
export const SET_USER_ID = 'SET_USER_ID';
export const SET_PORTAL_USER = 'SET_PORTAL_USER';
export const SET_READ_ONLY = 'SET_READ_ONLY';
export const CONNECTION_STATE = 'CONNECTION_STATE';
export const SET_EXPANDED_COMPONENT = 'SET_EXPANDED_COMPONENT';

export type MiscActionTypes = 'UPDATE_LOADING' | 'LOGIN_ERROR_RECOGNIZED' | 'LOGIN' | 'LOGIN_RESULT' | 'LOGIN_STATE' |
    'LOGIN_RETRY_REQUESTED' | 'SET_LOADING_MESSAGE' | 'SHOW_TASK' | 'SHOW_TASK_HANDLED' | 'SAVE_OPEN_TASK' |
    'SET_RESTORE_STATUS' | 'SET_FIRM_ID' | 'SET_USER_ID' | 'SET_PORTAL_USER' | 'SET_READ_ONLY' | 'CONNECTION_STATE' |
    'SET_EXPANDED_COMPONENT';

export enum RestoreStatus {
    NotRestored = 1,
    Restoring = 2,
    Restored = 3
}

export type RestoreStatusType = typeof RestoreStatus[keyof typeof RestoreStatus];

export type MiscAction = {
    type: MiscActionTypes;
    isLoading: boolean;
    isLoggingIn: boolean;
    loadingMessage: string;
    isReadOnly: boolean;
    firmId: string;
    userId: string;
    portalUser: boolean;
    restoreStatus: RestoreStatusType;
    payload: LoginAction;
    result: boolean;
    isConnected: boolean;
    task: Task;
    component: string;
    error: string;
}

export type MiscActions = {
    setConnectionState: (isConnected: number) => { isConnected: number; type: 'CONNECTION_STATE'; };
    updateLoading: (isLoading: boolean) => { isLoading: boolean; type: 'UPDATE_LOADING'; };
    setLoadingMessage: (msg: string) => { loadingMessage: string; type: 'SET_LOADING_MESSAGE'; };
    setReadOnly: (isReadOnly: boolean) => { isReadOnly: boolean; type: 'SET_READ_ONLY'; };
    setFirmId: (firmId: string) => { firmId: string; type: 'SET_FIRM_ID'; };
    setUserId: (userId: string) => { type: 'SET_USER_ID'; userId: string; };
    setPortalUser: (portalUser: boolean) => { type: 'SET_PORTAL_USER'; portalUser: boolean; };
    setRestoreStatus: (status: RestoreStatusType) => { type: 'SET_RESTORE_STATUS'; restoreStatus: number; };
    login: (username: string, password: string, isInitial?: boolean) => { type: string; payload: LoginAction; };
    loginResult: (result: boolean) => { type: 'LOGIN_RESULT'; result: boolean; };
    loginErrorRecognized: () => { type: 'LOGIN_ERROR_RECOGNIZED'; };
    startLogin: () => { type: 'LOGIN_STATE'; isLoggingIn: true; };
    showTask: (task: Task) => { type: 'SHOW_TASK'; task: Task; };
    showTaskHandled: () => { type: 'SHOW_TASK_HANDLED'; };
    saveOpenTask: (task: Task) => { type: string; task: Task; };
    setExpandedComponent: (component: string) => { type: 'SET_EXPANDED_COMPONENT'; component: string; };
    setLoginState: (isLoggingIn: boolean) => { type: 'LOGIN_STATE'; isLoggingIn: boolean; };
    setInitialLogin: (isInitial: boolean) => { type: 'LOGIN_STATE'; isInitial: boolean; };
    setLoginError: (error: string) => { type: 'LOGIN_STATE'; error: string; };
}

export const NOT_RESTORED = 1;
export const RESTORING = 2;
export const RESTORED = 3;

/**
 * Set the connection state.
 * @param {number} isConnected
 * @returns {{isConnected: boolean, type: 'CONNECTION_STATE'}}
 */
export const setConnectionState = (isConnected: boolean): { isConnected: boolean; type: 'CONNECTION_STATE'; } => {
    return {type: CONNECTION_STATE, isConnected};
}

/**
 * Update loading status with true (still loading) or false (done loading).
 *
 * @param {boolean} isLoading
 * @returns {{isLoading: boolean, type: 'UPDATE_LOADING'}}
 */
export const updateLoading = (isLoading: boolean): { isLoading: boolean; type: 'UPDATE_LOADING'; } => {
    return {type: UPDATE_LOADING, isLoading};
};

/**
 * Set a loading message.
 *
 * @param {string} msg
 * @returns {{loadingMessage: string, type: 'SET_LOADING_MESSAGE'}}
 */
export const setLoadingMessage = (msg: string): { loadingMessage: string; type: 'SET_LOADING_MESSAGE'; } => {
    return {type: SET_LOADING_MESSAGE, loadingMessage: msg};
};

/**
 * Set read-only mode
 * @param {boolean} isReadOnly
 * @returns {{isReadOnly: boolean, type: 'SET_READ_ONLY'}}
 */
export const setReadOnly = (isReadOnly: boolean): { isReadOnly: boolean; type: 'SET_READ_ONLY'; } => {
    return {type: SET_READ_ONLY, isReadOnly};
}

/**
 * Set the firm ID for the logged-in user.
 *
 * @param {uuid} firmId
 * @returns {{firmId: string, type: 'SET_FIRM_ID'}}
 */
export const setFirmId = (firmId: string): { firmId: string; type: 'SET_FIRM_ID'; } => {
    return {type: SET_FIRM_ID, firmId};
};

/**
 * Set the user ID for the logged-in user.
 *
 * @param {uuid} userId
 * @returns {{type: 'SET_USER_ID', userId: string}}
 */
export const setUserId = (userId: string): { type: 'SET_USER_ID'; userId: string; } => {
    return {type: SET_USER_ID, userId};
};

/**
 * Set the portalUser flag.
 *
 * @param {boolean} portalUser
 * @returns {{type: 'SET_PORTAL_USER', portalUser: boolean}}
 */
export const setPortalUser = (portalUser: boolean): { type: 'SET_PORTAL_USER'; portalUser: boolean; } => {
    return {type: SET_PORTAL_USER, portalUser};
}

/**
 * Set restore status.
 *
 * @param {number} status
 * @return {{type: 'SET_RESTORE_STATUS', restoreStatus: number}}
 */
export const setRestoreStatus = (status: RestoreStatusType): { type: 'SET_RESTORE_STATUS'; restoreStatus: number; } => {
    return {type: SET_RESTORE_STATUS, restoreStatus: status};
};

export type LoginActionFields = {
    username: string;
    password: string;
    isInitial: boolean;
};

export type LoginAction = {
    type: typeof LOGIN,
    payload: LoginActionFields;
};

/**
 *
 * @param {string} username
 * @param {string} password
 * @param {boolean} [isInitial]
 * @returns {LoginAction}
 */
export const login = (username: string, password: string, isInitial: boolean): LoginAction => {
    return {type: LOGIN, payload: {username, password, isInitial}};
};

export type LoginError = {title: string; message: string};

export interface LoginResultFields {
    isAuthenticated: boolean;
    authenticationError: LoginError | string;
    firmId: string;
    userId: string;
}

export interface LoginResult {
    type: typeof LOGIN_RESULT,
    payload: LoginResultFields;
}

/**
 * Handle an authentication result from miscSagas
 *
 * @param {boolean} isAuthenticated
 * @param {string} authenticationError
 * @param {uuid} firmId
 * @param {uuid} userId
 * @returns {LoginResult}
 */
export const loginResult = (isAuthenticated: boolean, authenticationError: LoginError | string, firmId: string, userId: string): LoginResult => {
    return {type: LOGIN_RESULT, payload: {isAuthenticated, authenticationError, firmId, userId}};
};

export const loginErrorRecognized = () => {
    return {type: LOGIN_ERROR_RECOGNIZED};
};

/**
 * Request that the post-login orchestration sequence be re-run after a
 * recoverable failure (e.g. transient API outage during initial workspace
 * load). The miscReducer increments `loginRetryRequestId` so that
 * `usePostLoginOrchestration` can react to the change.
 */
export const requestLoginRetry = () => {
    return {type: LOGIN_RETRY_REQUESTED};
};

export const startLogin = () => {
    return {type: LOGIN_STATE, isLoggingIn: true};
};

export const stopLogin = () => {
    return {type: LOGIN_STATE, isLoggingIn: false};
};

/**
 * Call to tell TaskItem to save its work.
 *
 * @param {Task} task
 * @return {{task: Task, type: string}}
 */
export const saveOpenTask = (task: Task): { type: string; task: Task; } => {
    return {type: SAVE_OPEN_TASK, task};
};

/**
 * Call when a new task is to be shown in TaskGrid.
 *
 * @param {Task} task
 * @return {{task: Task, type: string}}
 */
export const showTask = (task: Task): { task: Task; type: string; } => {
    return {type: SHOW_TASK, task};
};

/**
 * Call to acknowledge that the new task has been shown.
 *
 * @return {{type: 'SET_SHOW_HANDLED'}}
 */
export const showTaskHandled = (): { type: 'SHOW_TASK_HANDLED'; } => {
    return {type: SHOW_TASK_HANDLED};
};
